import React from "react";
import {
  Box,
  Typography,
  Button,
  CssBaseline
} from "@mui/material";
import { useHistory } from "react-router-dom";

const CAREERS_WHITE = "#FFFDF8";
const CAREERS_GRAY = "#B8B8B8";
const CAREERS_MUTED = "#777777";

function CareersPage() {
  const history = useHistory();

  const sectionTitleSx = {
    fontSize: {
      xs: "1.35rem",
      md: "1.6rem"
    },
    fontWeight: 400,
    letterSpacing: "-0.01em",
    color: CAREERS_WHITE,
    mb: 2
  };

  const bodySx = {
    fontSize: {
      xs: "0.95rem",
      md: "1rem"
    },
    lineHeight: 1.8,
    fontWeight: 300,
    color: CAREERS_GRAY,
    mb: 2.5
  };

  return (
    <>
      <CssBaseline />

      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#000",
          color: CAREERS_WHITE
        }}
      >
        {/* =====================================================
            HEADER
            ===================================================== */}

        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,

            bgcolor: "rgba(0, 0, 0, 0.94)",
            backdropFilter: "blur(12px)",

            borderBottom: "1px solid #222",

            px: {
              xs: 3,
              md: 6
            },

            py: 2.5,

            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          {/* Logo */}

          <Box
            component="button"
            type="button"
            onClick={() => history.push("/")}
            aria-label="Return to Colloquia"
            sx={{
              border: 0,
              background: "transparent",
              padding: 0,
              cursor: "pointer",

              display: "flex",
              alignItems: "center"
            }}
          >
            <Box
              component="img"
              src="/Colloquia_Logo.png"
              alt="Colloquia"
              sx={{
                height: {
                  xs: 55,
                  md: 65
                },

                width: "auto",

                borderRadius: "12px"
              }}
            />
          </Box>

          <Button
            onClick={() => history.push("/")}
            sx={{
              color: CAREERS_GRAY,
              fontWeight: 300,
              textTransform: "none",
              fontSize: "0.9rem",

              "&:hover": {
                color: CAREERS_WHITE,
                bgcolor: "transparent"
              }
            }}
          >
            Return to Colloquia
          </Button>
        </Box>

        {/* =====================================================
            MAIN CONTENT
            ===================================================== */}

        <Box
          component="main"
          sx={{
            width: "100%",
            maxWidth: 920,

            mx: "auto",

            px: {
              xs: 4,
              sm: 6,
              md: 8
            },

            pt: {
              xs: 8,
              md: 12
            },

            pb: {
              xs: 10,
              md: 14
            }
          }}
        >
          {/* =====================================================
              PAGE INTRO
              ===================================================== */}

          <Box
            sx={{
              mb: {
                xs: 7,
                md: 9
              }
            }}
          >
            <Typography
              component="h1"
              sx={{
                fontSize: {
                  xs: "2.5rem",
                  sm: "3.2rem",
                  md: "4rem"
                },

                lineHeight: 1.1,
                fontWeight: 300,
                letterSpacing: "-0.035em",

                color: CAREERS_WHITE,

                mb: 2
              }}
            >
              Careers
            </Typography>

            <Typography
              sx={{
                maxWidth: 720,

                fontSize: {
                  xs: "1rem",
                  md: "1.1rem"
                },

                lineHeight: 1.75,
                fontWeight: 300,

                color: CAREERS_GRAY
              }}
            >
              We're building Colloquia for people who believe that
              intelligence, knowledge, competence, and good ideas
              deserve greater influence in society.
            </Typography>
          </Box>

          {/* =====================================================
              CURRENT OPENINGS
              ===================================================== */}

          <Box
            sx={{
              border: "1px solid #292929",
              borderRadius: "16px",

              bgcolor: "#080808",

              p: {
                xs: 4,
                md: 5
              },

              mb: {
                xs: 8,
                md: 10
              }
            }}
          >
            <Typography
              sx={{
                fontSize: "0.75rem",
                letterSpacing: "0.16em",
                textTransform: "uppercase",

                color: CAREERS_MUTED,

                fontWeight: 400,

                mb: 2
              }}
            >
              Current openings
            </Typography>

            <Typography
              component="h2"
              sx={{
                fontSize: {
                  xs: "1.5rem",
                  md: "1.8rem"
                },

                lineHeight: 1.3,
                fontWeight: 300,

                color: CAREERS_WHITE,

                mb: 2
              }}
            >
              No open positions at this time
            </Typography>

            <Typography
              sx={{
                fontSize: {
                  xs: "0.95rem",
                  md: "1rem"
                },

                lineHeight: 1.8,
                fontWeight: 300,

                color: CAREERS_GRAY,

                maxWidth: 700
              }}
            >
              Colloquia does not currently have any open positions.
              We expect our team and hiring needs to evolve as the
              company grows, so this page will be updated when new
              opportunities become available.
            </Typography>
          </Box>

          {/* =====================================================
              WHAT WE VALUE
              ===================================================== */}

          <Box sx={{ mb: 9 }}>
            <Typography sx={sectionTitleSx}>
              What we value
            </Typography>

            <Typography sx={bodySx}>
              We are interested in people who take ideas seriously,
              think independently, and care deeply about the quality
              of the systems they build.
            </Typography>

            <Typography sx={bodySx}>
              Colloquia operates at the intersection of technology,
              social media, algorithms, intellectual culture, and
              political discourse. As the company grows, we expect
              to look for exceptional people across engineering,
              product, design, research, operations, and other
              disciplines.
            </Typography>

            <Typography sx={bodySx}>
              We value intellectual curiosity, technical ability,
              sound judgment, ambition, integrity, and the willingness
              to challenge assumptions—including our own.
            </Typography>
          </Box>

          {/* =====================================================
              FUTURE OPPORTUNITIES
              ===================================================== */}

          <Box sx={{ mb: 9 }}>
            <Typography sx={sectionTitleSx}>
              Future opportunities
            </Typography>

            <Typography sx={bodySx}>
              When we begin hiring, open positions will be listed
              here with detailed descriptions of the role,
              responsibilities, qualifications, and application
              instructions.
            </Typography>

            <Typography sx={bodySx}>
              If there is a future opportunity that aligns with
              your background, we encourage you to check back
              periodically.
            </Typography>
          </Box>

          {/* =====================================================
              CONTACT
              ===================================================== */}

          <Box sx={{ mb: 10 }}>
            <Typography sx={sectionTitleSx}>
              Stay connected
            </Typography>

            <Typography sx={bodySx}>
              Although we are not currently accepting applications,
              you can learn more about Colloquia and follow the
              company through our official channels.
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                mt: 3
              }}
            >
              <Button
                variant="outlined"
                onClick={() => history.push("/about")}
                sx={{
                  color: CAREERS_WHITE,
                  borderColor: "#444",

                  textTransform: "none",
                  fontWeight: 300,

                  "&:hover": {
                    borderColor: "#777",
                    bgcolor: "#111"
                  }
                }}
              >
                About Colloquia
              </Button>

              <Button
                variant="outlined"
                onClick={() => history.push("/contact")}
                sx={{
                  color: CAREERS_WHITE,
                  borderColor: "#444",

                  textTransform: "none",
                  fontWeight: 300,

                  "&:hover": {
                    borderColor: "#777",
                    bgcolor: "#111"
                  }
                }}
              >
                Contact Us
              </Button>
            </Box>
          </Box>

          {/* =====================================================
              FOOTER NAVIGATION
              ===================================================== */}

          <Box
            sx={{
              borderTop: "1px solid #222",

              pt: 4,

              display: "flex",
              flexWrap: "wrap",

              gap: {
                xs: 0.5,
                md: 1
              }
            }}
          >
            {[
              ["About", "/about"],
              ["Terms", "/terms"],
              ["Privacy", "/privacy"],
              ["Careers", "/careers"],
              ["Support", "/support"],
              ["Contact", "/contact"]
            ].map(([label, path]) => (
              <Button
                key={label}
                onClick={() => history.push(path)}
                sx={{
                  color:
                    label === "Careers"
                      ? CAREERS_WHITE
                      : CAREERS_MUTED,

                  textTransform: "none",
                  fontWeight: 300,
                  fontSize: "0.9rem",

                  minWidth: "auto",
                  px: 1,

                  "&:hover": {
                    color: CAREERS_WHITE,
                    bgcolor: "transparent"
                  }
                }}
              >
                {label}
              </Button>
            ))}
          </Box>

          <Typography
            sx={{
              mt: 3,
              color: "#555",
              fontSize: "0.8rem",
              fontWeight: 300
            }}
          >
            © 2026 Colloquia from Penneferies Squire Capital
          </Typography>
        </Box>
      </Box>
    </>
  );
}

export default CareersPage;